import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useLanguage } from '../lib/language';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import Sidebar from '../components/Sidebar';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Loader2, Send, ArrowLeft, Search, Plus, MessageSquare,
  Mail, User, AlertCircle, Check, CheckCheck, Clock,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffDays === 0) {
    return d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
  }
  if (diffDays === 1) return 'Wczoraj';
  if (diffDays < 7) {
    return d.toLocaleDateString('pl-PL', { weekday: 'short' });
  }
  return d.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit' });
};

const formatFullDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString('pl-PL', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

export default function MessagesPage() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingThread, setLoadingThread] = useState(false);
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState('');
  const [replyText, setReplyText] = useState('');
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [mobileShowThread, setMobileShowThread] = useState(false);

  const messagesEndRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Fetch threads
  const fetchThreads = useCallback(async () => {
    try {
      const params = search ? { search } : {};
      const res = await axios.get(`${API}/messages/threads`, { params });
      setThreads(res.data.threads || []);
    } catch (err) {
      console.error('Failed to fetch threads');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    fetchThreads();
  }, [fetchThreads]);

  // Open thread from URL param (?client=...)
  useEffect(() => {
    const clientId = searchParams.get('client');
    if (clientId && !loading) {
      openClientThread(clientId);
    }
  }, [searchParams, loading]);

  const openClientThread = async (clientId) => {
    try {
      setLoadingThread(true);
      const res = await axios.get(`${API}/messages/client/${clientId}`);
      if (res.data.thread) {
        setSelectedThread(res.data.thread);
        setMessages(res.data.messages || []);
        setMobileShowThread(true);
        // Mark as read
        if (res.data.thread.unread_count > 0) {
          await axios.put(`${API}/messages/threads/${res.data.thread.id}/read`);
          fetchThreads();
        }
      } else {
        // No thread yet — open new message dialog for this client
        setShowNewDialog(true);
      }
    } catch (err) {
      console.error('Failed to open client thread');
    } finally {
      setLoadingThread(false);
    }
  };

  const selectThread = async (thread) => {
    setSelectedThread(thread);
    setMobileShowThread(true);
    setLoadingThread(true);
    setReplyText('');

    try {
      const res = await axios.get(`${API}/messages/threads/${thread.id}`);
      setMessages(res.data.messages || []);

      // Mark as read
      if (thread.unread_count > 0) {
        await axios.put(`${API}/messages/threads/${thread.id}/read`);
        setThreads(prev => prev.map(t =>
          t.id === thread.id ? { ...t, unread_count: 0 } : t
        ));
      }
    } catch (err) {
      toast.error('Nie udalo sie zaladowac wiadomosci');
    } finally {
      setLoadingThread(false);
      setTimeout(scrollToBottom, 100);
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedThread) return;
    setSending(true);

    try {
      const res = await axios.post(`${API}/messages/threads/${selectedThread.id}/reply`, {
        client_id: selectedThread.client_id,
        body_text: replyText,
      });

      setMessages(prev => [...prev, res.data.message]);
      setReplyText('');
      fetchThreads();
      setTimeout(scrollToBottom, 100);

      if (res.data.message.status === 'failed') {
        toast.error(language === 'pl' ? 'Blad wysylki email' : 'Email send failed');
      }
    } catch (err) {
      toast.error(language === 'pl' ? 'Nie udalo sie wyslac' : 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendReply();
    }
  };

  const goBackToList = () => {
    setMobileShowThread(false);
    setSelectedThread(null);
    setMessages([]);
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar activePath="/messages" />

      <main className="lg:ml-64 p-4 lg:p-6">
        <h1 className="text-2xl font-bold mb-4 flex items-center gap-2">
          <MessageSquare className="w-6 h-6" />
          {language === 'pl' ? 'Wiadomosci' : 'Messages'}
        </h1>

        <div className="flex gap-4 h-[calc(100vh-130px)]">
          {/* Thread List — hidden on mobile when viewing a thread */}
          <div className={`w-full lg:w-1/3 flex flex-col ${mobileShowThread ? 'hidden lg:flex' : 'flex'}`}>
            {/* Search + New */}
            <div className="flex gap-2 mb-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder={language === 'pl' ? 'Szukaj...' : 'Search...'}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 h-10"
                />
              </div>
              <Button size="icon" onClick={() => setShowNewDialog(true)} title={language === 'pl' ? 'Nowa wiadomosc' : 'New message'}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>

            {/* Thread items */}
            <div className="flex-1 overflow-y-auto space-y-1">
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : threads.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">{language === 'pl' ? 'Brak wiadomosci' : 'No messages'}</p>
                </div>
              ) : (
                threads.map(thread => (
                  <button
                    key={thread.id}
                    onClick={() => selectThread(thread)}
                    className={`w-full text-left p-3 rounded-lg transition-colors ${
                      selectedThread?.id === thread.id
                        ? 'bg-secondary'
                        : 'hover:bg-secondary/50'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-bold text-primary">
                          {thread.client_name?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className={`text-sm truncate ${thread.unread_count > 0 ? 'font-bold' : 'font-medium'}`}>
                            {thread.client_name}
                          </span>
                          <span className="text-xs text-muted-foreground flex-shrink-0 ml-2">
                            {formatDate(thread.last_message_at)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between mt-0.5">
                          <p className={`text-xs truncate ${thread.unread_count > 0 ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                            {thread.last_message_direction === 'outbound' && (
                              <span className="text-muted-foreground mr-1">Ty:</span>
                            )}
                            {thread.last_message_preview}
                          </p>
                          {thread.unread_count > 0 && (
                            <Badge variant="default" className="ml-2 text-xs h-5 min-w-5 flex items-center justify-center">
                              {thread.unread_count}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Conversation View */}
          <div className={`w-full lg:w-2/3 flex flex-col ${!mobileShowThread ? 'hidden lg:flex' : 'flex'}`}>
            {selectedThread ? (
              <>
                {/* Header */}
                <div className="flex items-center gap-3 pb-3 border-b border-border">
                  <Button variant="ghost" size="icon" className="lg:hidden" onClick={goBackToList}>
                    <ArrowLeft className="w-5 h-5" />
                  </Button>
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-sm font-bold text-primary">
                      {selectedThread.client_name?.charAt(0)?.toUpperCase() || '?'}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold truncate">{selectedThread.client_name}</h3>
                    <p className="text-xs text-muted-foreground truncate">{selectedThread.client_email}</p>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto py-4 space-y-3">
                  {loadingThread ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin" />
                    </div>
                  ) : (
                    <>
                      {messages.map((msg) => (
                        <motion.div
                          key={msg.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`max-w-[75%] rounded-xl px-4 py-2.5 ${
                            msg.direction === 'outbound'
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-secondary text-foreground'
                          }`}>
                            <p className="text-sm whitespace-pre-wrap">{msg.body_text}</p>
                            <div className={`flex items-center gap-1 mt-1 text-[10px] ${
                              msg.direction === 'outbound' ? 'text-primary-foreground/70 justify-end' : 'text-muted-foreground'
                            }`}>
                              <span>{formatFullDate(msg.created_at)}</span>
                              {msg.direction === 'outbound' && (
                                msg.status === 'sent' ? <CheckCheck className="w-3 h-3" /> :
                                msg.status === 'failed' ? <AlertCircle className="w-3 h-3 text-red-300" /> :
                                msg.status === 'sending' ? <Clock className="w-3 h-3" /> :
                                <Check className="w-3 h-3" />
                              )}
                            </div>
                          </div>
                        </motion.div>
                      ))}
                      <div ref={messagesEndRef} />
                    </>
                  )}
                </div>

                {/* Reply Input */}
                <div className="border-t border-border pt-3">
                  <div className="flex gap-2">
                    <Textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder={language === 'pl' ? 'Napisz wiadomosc...' : 'Type a message...'}
                      rows={2}
                      className="resize-none flex-1"
                    />
                    <Button
                      onClick={handleSendReply}
                      disabled={sending || !replyText.trim()}
                      size="icon"
                      className="h-auto"
                    >
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>{language === 'pl' ? 'Wybierz watek lub rozpocznij nowa rozmowe' : 'Select a thread or start a new conversation'}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* New Message Dialog */}
        <AnimatePresence>
          {showNewDialog && (
            <NewMessageDialog
              language={language}
              onClose={() => setShowNewDialog(false)}
              onSent={(threadId) => {
                setShowNewDialog(false);
                fetchThreads();
                // Select the new thread
                setTimeout(async () => {
                  const res = await axios.get(`${API}/messages/threads`);
                  const newThreads = res.data.threads || [];
                  setThreads(newThreads);
                  const newThread = newThreads.find(t => t.id === threadId);
                  if (newThread) selectThread(newThread);
                }, 300);
              }}
            />
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}


function NewMessageDialog({ language, onClose, onSent }) {
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingClients, setLoadingClients] = useState(true);

  useEffect(() => {
    axios.get(`${API}/clients`)
      .then(res => setClients(res.data.clients || res.data || []))
      .catch(() => toast.error('Failed to load clients'))
      .finally(() => setLoadingClients(false));
  }, []);

  const handleSend = async () => {
    if (!selectedClientId || !body.trim()) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/messages/send`, {
        client_id: selectedClientId,
        subject: subject || undefined,
        body_text: body,
      });
      toast.success(language === 'pl' ? 'Wiadomosc wyslana' : 'Message sent');
      onSent(res.data.thread_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setSending(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-card border border-border rounded-xl p-6 w-full max-w-lg mx-4 shadow-xl"
      >
        <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
          <Mail className="w-5 h-5" />
          {language === 'pl' ? 'Nowa wiadomosc' : 'New Message'}
        </h2>

        <div className="space-y-4">
          {/* Client select */}
          <div className="space-y-2">
            <Label>{language === 'pl' ? 'Klient' : 'Client'}</Label>
            {loadingClients ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading...
              </div>
            ) : (
              <select
                value={selectedClientId}
                onChange={(e) => setSelectedClientId(e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              >
                <option value="">{language === 'pl' ? '-- Wybierz klienta --' : '-- Select client --'}</option>
                {clients.map(c => (
                  <option key={c.id} value={c.id}>{c.name} ({c.email})</option>
                ))}
              </select>
            )}
          </div>

          {/* Subject */}
          <div className="space-y-2">
            <Label>{language === 'pl' ? 'Temat (opcjonalnie)' : 'Subject (optional)'}</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={language === 'pl' ? 'Temat wiadomosci' : 'Message subject'}
            />
          </div>

          {/* Body */}
          <div className="space-y-2">
            <Label>{language === 'pl' ? 'Tresc' : 'Body'}</Label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={language === 'pl' ? 'Napisz wiadomosc...' : 'Write your message...'}
              rows={5}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose}>
            {language === 'pl' ? 'Anuluj' : 'Cancel'}
          </Button>
          <Button onClick={handleSend} disabled={sending || !selectedClientId || !body.trim()}>
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            {language === 'pl' ? 'Wyslij' : 'Send'}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}
